/**
 * BhiComplianceBadges — iter1026
 *
 * Малка sticky секция с EU AI Act Article 50 + GDPR Art. 9 transparency
 * chip-ове, която седи в началото на BHI страницата.
 *
 * Целта: пациентът да види от първия момент че:
 *   • Платформата е образователна, НЕ медицинско изделие
 *   • Класифицирана е като EU AI Act Art. 50 (ограничен риск)
 *   • Данните са under GDPR Art. 9(2)(a) explicit consent
 *   • Може да експортва / изтрие данните си
 */
import React, { useState } from "react";
import { ShieldCheck, Sparkles, Info, ExternalLink } from "lucide-react";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";

export default function BhiComplianceBadges() {
  const { isDark } = useTheme();
  const { language } = useAuth();
  const bg = (language || "bg") === "bg";
  const [expanded, setExpanded] = useState(false);

  const chipsCls = isDark
    ? "bg-white/5 border-white/10 text-gray-200"
    : "bg-white border-stone-200 text-stone-700";
  const badgeGreen = isDark
    ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/30"
    : "bg-emerald-50 text-emerald-800 border-emerald-200";
  const badgeSky = isDark
    ? "bg-sky-500/15 text-sky-200 border-sky-500/30"
    : "bg-sky-50 text-sky-800 border-sky-200";
  const badgePurple = isDark
    ? "bg-purple-500/15 text-purple-200 border-purple-500/30"
    : "bg-purple-50 text-purple-800 border-purple-200";

  return (
    <div
      data-testid="bhi-compliance-badges"
      className={`mb-4 rounded-2xl border ${chipsCls}`}
    >
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 flex items-center gap-2 flex-wrap text-left"
        data-testid="bhi-compliance-toggle"
      >
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${badgeGreen}`}>
          <ShieldCheck className="w-3 h-3" />
          {bg ? "GDPR Art. 9(2)(a)" : "GDPR Art. 9(2)(a)"}
        </span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${badgeSky}`}>
          🇪🇺 {bg ? "EU AI Act · Art. 50 · Ограничен риск" : "EU AI Act · Art. 50 · Limited risk"}
        </span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${badgePurple}`}>
          <Sparkles className="w-3 h-3" />
          {bg ? "Образователно · НЕ е мед. изделие" : "Educational · NOT a medical device"}
        </span>
        <Info className="w-3.5 h-3.5 ml-auto opacity-60" />
      </button>
      {expanded && (
        <div className="px-3 pb-3 text-[11px] leading-relaxed space-y-2 border-t border-stone-200 dark:border-white/10 pt-2">
          <div>
            <strong>{bg ? "Правно основание:" : "Legal basis:"}</strong>{" "}
            {bg
              ? "Здравните Ви данни се обработват на основание Ваше explicit consent (GDPR Art. 9(2)(a)). Всяка декларирана диагноза / risk factor изисква Ваше потвърждение преди да влезе в BHI изчисленията."
              : "Your health data is processed under your explicit consent (GDPR Art. 9(2)(a)). Every declared diagnosis / risk factor requires your confirmation before it enters BHI calculations."}
          </div>
          <div>
            <strong>{bg ? "AI класификация:" : "AI classification:"}</strong>{" "}
            {bg
              ? "Платформата е класифицирана като AI система с ограничен риск (EU AI Act Art. 50). Всеки резултат от AI носи transparency-chip и подлежи на човешки oversight (Ваш лекар / клиницист)."
              : "The platform is classified as a limited-risk AI system (EU AI Act Art. 50). Every AI output carries a transparency chip and remains under human oversight (your physician / clinician)."}
          </div>
          <div>
            <strong>{bg ? "Вашите права:" : "Your rights:"}</strong>{" "}
            {bg
              ? "Достъп, преносимост, изтриване (Art. 15/17/20) — в"
              : "Access, portability, erasure (Art. 15/17/20) — under"}
            {" "}
            <a href="/dashboard/profile" className="underline inline-flex items-center gap-0.5">
              {bg ? "Профил → Сигурност" : "Profile → Security"}
              <ExternalLink className="w-2.5 h-2.5" />
            </a>
          </div>
          <div className="opacity-70">
            <strong>{bg ? "Данни не се използват за:" : "Data is NOT used for:"}</strong>{" "}
            {bg
              ? "AI training, продажба, споделяне без Ваше explicit съгласие."
              : "AI training, sale, or sharing without your explicit consent."}
          </div>
        </div>
      )}
    </div>
  );
}
